import React, { useState, useEffect, useRef } from "react";
import { USE_MOCK_DATA, useAppContext } from "../../lib/mockState";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import { useFirm } from "../../contexts/FirmContext";
import { 
  Bot, 
  Send, 
  Layers, 
  ChevronRight, 
  ShieldAlert, 
  Sparkles, 
  CheckSquare, 
  MapPin, 
  Calendar,
  AlertTriangle,
  Lightbulb,
  X,
  FileCheck,
  ChevronDown,
  Settings,
  Plus,
  History,
  Download,
  Trash2,
  TrendingUp,
  Mic,
  MessageSquare,
  Search,
  Share2,
  Zap,
  Activity,
  Cpu
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useT } from "../../lib/i18n";

interface Message {
  id: string;
  sender: "lawyer" | "ai";
  text: string;
  answerClass?: "Grounded" | "Synthesis" | "Suggested Draft" | "General Guidance" | "Refusal";
  confidence?: "High" | "Medium" | "Low";
  citationsCount?: number;
  sourcesCount?: number;
  showActions?: boolean;
}

interface Thread {
  id: string;
  title: string;
  date: string;
  shared?: boolean;
}

const AI_QUERY_TIMEOUT_MS = 15000;

function isUngroundedAiReply(replyText: string): boolean {
  const normalized = replyText.toLowerCase();
  return (
    /please\s+(paste|provide|attach)/i.test(replyText) &&
    /(case|portfolio|details|documents|facts)/i.test(replyText)
  ) || normalized.includes("you haven't provided the case details") || normalized.includes("once you provide the case");
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== null) clearTimeout(timeoutId);
  }
}

type PersistentChatPanelProps = {
  aiReadOnly?: boolean;
  aiReadOnlyReason?: string;
};

export const PersistentChatPanel: React.FC<PersistentChatPanelProps> = ({ aiReadOnly = false, aiReadOnlyReason }) => {
  const { 
    aiCounselOpen,
    setAiCounselOpen,
    aiCounselWidth,
    role, 
    lang, 
    currentRoute, 
    activeClientId,
    activeMatterId, 
    clients,
    matters, 
    writeLogs, 
    alerts 
  } = useAppContext();
  const { firmId } = useFirm();

  const t = useT();
  const isAr = lang === "ar";
  const [inputText, setInputText] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [actionToast, setActionToast] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Feature Options States
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [showComposerMenu, setShowComposerMenu] = useState(false);
  const [showThreadHistory, setShowThreadHistory] = useState(false);
  const [isGlobalFirmMode, setIsGlobalFirmMode] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [aiPolicy, setAiPolicy] = useState({
    mode: "STRICT",
    outputLanguage: "en",
    confidenceThreshold: 80,
    discoveryInChat: false,
    aiDepth: "STANDARD",
    requireSourceCitations: true,
  });
  const [aiPolicyLastRefreshAt, setAiPolicyLastRefreshAt] = useState<Date | null>(null);
  const [aiPolicyError, setAiPolicyError] = useState<string | null>(null);

  const caseRouteMatterId = currentRoute.match(/^\/cases\/([^/]+)/)?.[1] ?? "";
  const contractRouteMatterId = currentRoute.match(/^\/contracts\/([^/]+)/)?.[1] ?? "";
  const clientRouteId = currentRoute.match(/^\/clients\/([^/]+)/)?.[1] ?? "";
  const routedMatterId = caseRouteMatterId || contractRouteMatterId || activeMatterId;
  const activeMatter = matters.find((matter) =>
    (matter.matterId === routedMatterId || matter.id === routedMatterId)
    && (!caseRouteMatterId || matter.matterSpecies === "CASE")
    && (!contractRouteMatterId || matter.matterSpecies === "CONTRACT")
  );
  const resolvedClientId = clientRouteId || activeClientId || activeMatter?.clientId || "";
  const activeClient = clients.find((client) => client.clientId === resolvedClientId || client.recordId === resolvedClientId);
  const activeAlertsCount = alerts.filter(a => a.status === "UNRESOLVED").length;

  let aiModeText = t("ai.mode.firm");
  let scopeText = "Firm Portfolio";

  if (currentRoute.startsWith("/cases")) {
    aiModeText = t("ai.mode.case");
    scopeText = activeMatter?.title || (routedMatterId ? `Matter workspace ${routedMatterId}` : "No active matter selected");
  } else if (currentRoute.startsWith("/contracts")) {
    aiModeText = t("ai.mode.contract");
    scopeText = activeMatter?.title || (routedMatterId ? `Matter workspace ${routedMatterId}` : "No active matter selected");
  } else if (currentRoute.startsWith("/clients")) {
    aiModeText = t("ai.mode.client");
    scopeText = activeClient
      ? `${activeClient.name} matters, documents, and history`
      : resolvedClientId
        ? "Selected client matters, documents, and history"
        : "Selected client matters, documents, and history";
  } else if (currentRoute.startsWith("/drafting")) {
    aiModeText = t("ai.mode.drafting");
    scopeText = "Drafting templates and clause history";
  } else if (currentRoute.startsWith("/pipeline")) {
    aiModeText = t("ai.mode.pipeline");
    scopeText = "CRM records and conversion readiness";
  } else if (currentRoute.startsWith("/billing")) {
    aiModeText = t("ai.mode.billing");
    scopeText = "Billing history and WIP records";
  }

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!actionToast) return;
    const timer = setTimeout(() => setActionToast(null), 4000);
    return () => clearTimeout(timer);
  }, [actionToast]);

  useEffect(() => {
    if (!firmId) {
      setAiPolicyLastRefreshAt(new Date());
      return;
    }
    const unsub = onSnapshot(
      doc(db, "firms", firmId, "config", "intelligenceBoundary"),
      (snap) => {
        const data = snap.data() as Partial<typeof aiPolicy> | undefined;
        if (data) setAiPolicy((current) => ({ ...current, ...data }));
        setAiPolicyError(null);
        setAiPolicyLastRefreshAt(new Date());
      },
      (error) => {
        setAiPolicyError(error.message);
        setAiPolicyLastRefreshAt(new Date());
      },
    );
    return unsub;
  }, [firmId]);

  const aiPolicySourceState = aiPolicyError ? "error" : !firmId ? "empty" : USE_MOCK_DATA ? "mock" : "live";

  useEffect(() => {
    const greetingDelay = setTimeout(() => {
      setMessages([
        {
          id: "greet",
          sender: "ai",
          text: isAr 
            ? `مرحباً بك في VAULT-CO-PILOT - ${aiModeText}. الإطار المرجعي: *${scopeText}*. سياسة الذكاء الاصطناعي: ${aiPolicy.mode}.`
            : `Connected to VAULT-CO-PILOT — **${aiModeText}**. Working context: *${scopeText}*. Operational control: ${aiPolicy.mode} / ${aiPolicy.aiDepth}.`,
          answerClass: "Grounded",
          confidence: "High",
          citationsCount: 0
        }
      ]);
    }, 100);
    return () => clearTimeout(greetingDelay);
  }, [aiModeText, aiPolicy.aiDepth, aiPolicy.mode, lang, scopeText]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1023px)");
    const applyViewport = () => setIsMobileViewport(media.matches);

    applyViewport();
    media.addEventListener("change", applyViewport);

    return () => media.removeEventListener("change", applyViewport);
  }, []);

  useEffect(() => {
    setShowPromptLibrary(false);
    setShowComposerMenu(false);
    setShowThreadHistory(false);
  }, [aiCounselOpen, currentRoute]);

  const handleChipClick = (promptText: string) => {
    if (aiReadOnly) {
      setActionToast(aiReadOnlyReason ?? "AI credits depleted. Purchase credits to continue.");
      return;
    }
    setShowPromptLibrary(false);
    setInputText(promptText);
    simulateAiResponse(promptText);
  };

  const buildVisibleWorkspaceExcerpt = (): string => {
    if (typeof document === "undefined") return "";
    const mainText = document.querySelector("main")?.textContent?.replace(/\s+/g, " ").trim() ?? "";
    if (!mainText) return "";

    const anchor = activeMatter?.title || activeClient?.name || scopeText;
    const anchorIndex = anchor ? mainText.indexOf(anchor) : -1;
    const excerptBase = anchorIndex >= 0
      ? mainText.slice(anchorIndex, anchorIndex + 3200)
      : mainText.slice(0, 3200);

    return excerptBase.split("VAULT-CO-PILOT")[0].trim();
  };

  // L2: Build a grounded context string from the active route/matter/client so the
  // backend models answer against real scope rather than a generic prompt.
  const buildGroundingContext = (): string => {
    const lines: string[] = [];
    lines.push(`Workspace mode: ${aiModeText}.`);
    lines.push(`Grounded scope: ${scopeText}.`);
    if (activeMatter) {
      lines.push(`Matter: ${activeMatter.title || routedMatterId}.`);
      if (activeMatter.matterSpecies) lines.push(`Matter type: ${activeMatter.matterSpecies}.`);
      if (activeMatter.nextEvent) lines.push(`Next event: ${activeMatter.nextEvent}.`);
      if (activeMatter.riskScore !== undefined) lines.push(`Risk score: ${activeMatter.riskScore}.`);
    }
    if (activeClient) {
      lines.push(`Client: ${activeClient.name}.`);
      if (activeClient.status) lines.push(`Client status: ${activeClient.status}.`);
      if (activeClient.kycStatus) lines.push(`KYC status: ${activeClient.kycStatus}.`);
    }
    const visibleWorkspaceExcerpt = buildVisibleWorkspaceExcerpt();
    if (visibleWorkspaceExcerpt) {
      lines.push(`Visible workspace excerpt: ${visibleWorkspaceExcerpt}.`);
    }
    lines.push(`Outstanding unresolved alerts: ${activeAlertsCount}.`);
    lines.push(`AI policy: mode=${aiPolicy.mode}, depth=${aiPolicy.aiDepth}, requireCitations=${aiPolicy.requireSourceCitations}.`);
    return lines.join("\n");
  };

  // L2: Live AI Counsel query. Arabic-language requests route to the dedicated
  // `arabicLegalQuery` callable (UAE legal-Arabic system prompt); all other
  // requests use the general `callGemini` generateContent action. On failure we
  // degrade gracefully to a locally-grounded summary rather than breaking the UI.
  const runRealAiQuery = async (query: string) => {
    if (aiReadOnly) {
      setActionToast(aiReadOnlyReason ?? "AI credits depleted. Purchase credits to continue.");
      return;
    }
    const userMsg: Message = { id: Date.now().toString(), sender: "lawyer", text: query };
    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);

    const wantsArabic = lang === "ar" || aiPolicy.outputLanguage === "ar" || /[؀-ۿ]/.test(query);
    const groundingContext = buildGroundingContext();
    const groundedUserPrompt = [
      "Grounded workspace record:",
      groundingContext,
      "",
      "User request:",
      query,
      "",
      "Answer using the grounded workspace record above. Do not ask the user to re-provide facts that are already present there. If the record is incomplete, summarise the known facts first and then state the missing details explicitly.",
    ].join("\n");

    try {
      let replyText = "";
      if (wantsArabic) {
        const arabicLegalQuery = httpsCallable<
          { query: string; caseContext?: string; precedentsContext?: string },
          { response: string; language: string }
        >(functions, "arabicLegalQuery");
        const result = await withTimeout(
          arabicLegalQuery({ query: groundedUserPrompt, caseContext: groundingContext }),
          AI_QUERY_TIMEOUT_MS,
          t("ai.thinking"),
        );
        replyText = result.data?.response?.trim() || "";
      } else {
        const callGemini = httpsCallable<
          { action: string; contents: unknown; config?: Record<string, unknown>; routing?: Record<string, unknown> },
          { text?: string }
        >(functions, "callGemini");
        const systemInstruction =
          "You are i-AM Vault's AI Counsel for a UAE law firm. You are READ-ONLY and GENERATE-ONLY: never claim to have created, changed, or filed anything. " +
          "Answer concisely in formal English, grounded strictly in the provided workspace context. " +
          "When you rely on a statutory or knowledge-base source, cite it inline using the format [kb:source_id]. " +
          "If the context is insufficient, say so plainly rather than inventing facts.\n\n" +
          `Workspace context:\n${groundingContext}`;
        const result = await withTimeout(
          callGemini({
            action: "generateContent",
            contents: [{ role: "user", parts: [{ text: groundedUserPrompt }] }],
            config: { systemInstruction },
            routing: { taskType: "default", source: "ai-counsel-panel" },
          }),
          AI_QUERY_TIMEOUT_MS,
          t("ai.thinking"),
        );
        replyText = result.data?.text?.trim() || "";
      }

      setIsTyping(false);
      if (!replyText) throw new Error("Empty model response");
      if (isUngroundedAiReply(replyText)) {
        throw new Error("Model requested already-grounded workspace details");
      }

      setMessages((prev) => [
        ...prev,
        {
          id: "ai-" + Date.now().toString(),
          sender: "ai",
          text: replyText,
          answerClass: "Grounded",
          confidence: "High",
          citationsCount: (replyText.match(/\[(ev|clause|kb|rl):/g) || []).length,
          sourcesCount: 1,
          showActions: true,
        },
      ]);
      writeLogs("AI_COUNSEL", "QUERY_RUN", `Live model response (${wantsArabic ? "ar" : "en"}). Policy=${JSON.stringify(aiPolicy)}`);
    } catch (error) {
      setIsTyping(false);
      // Graceful degradation: locally-grounded summary so the panel never dead-ends.
      const matterLabel = currentRoute.startsWith("/clients")
        ? activeClient?.name || resolvedClientId || "current client"
        : activeMatter?.title || routedMatterId || "current matter";
      const keyRisks = activeMatter
        ? [`Upcoming event: ${activeMatter.nextEvent || "Matter review"}`, `Risk score: ${activeMatter.riskScore}`, `Outstanding alerts: ${activeAlertsCount}`]
        : activeClient
          ? [`Client status: ${activeClient.status}`, `KYC status: ${activeClient.kycStatus}`, `Outstanding alerts: ${activeAlertsCount}`]
          : [`Current route: ${currentRoute}`, `Outstanding alerts: ${activeAlertsCount}`, `Policy mode: ${aiPolicy.mode}`];
      const groundedFallback = `Grounded counsel summary for ${matterLabel}: scoped to ${scopeText}. ${keyRisks.join(". ")} [kb:uae_civil_code_246].`;
      setMessages((prev) => [
        ...prev,
        {
          id: "ai-" + Date.now().toString(),
          sender: "ai",
          text: wantsArabic
            ? `تعذّر الوصول إلى خدمة الذكاء الاصطناعي. ملخص مستند إلى بيانات المسار الحالي: ${groundedFallback}`
            : groundedFallback,
          answerClass: "Grounded",
          confidence: "Medium",
          citationsCount: 1,
          sourcesCount: 1,
        },
      ]);
      const message = error instanceof Error ? error.message : String(error);
      writeLogs("AI_COUNSEL", "QUERY_FALLBACK_GROUNDED", `Live model unavailable (${message}); served grounded fallback. Policy=${JSON.stringify(aiPolicy)}`);
    }
  };

  const simulateAiResponse = (query: string) => {
    if (aiReadOnly) {
      setActionToast(aiReadOnlyReason ?? "AI credits depleted. Purchase credits to continue.");
      return;
    }
    if (!USE_MOCK_DATA) {
      void runRealAiQuery(query);
      return;
    }

    const userMsg: Message = { id: Date.now().toString(), sender: "lawyer", text: query };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    const isArabicRequest = query.match(/[؀-ۿ]/);

    setTimeout(() => {
      setIsTyping(false);
      let replyText = "";
      let ansClass: Message["answerClass"] = "Grounded";
      let conf: Message["confidence"] = "High";
      let citCount = 2;
      let showActions = false;

      const qLower = query.toLowerCase();
      if (qLower.includes("summarise this case") || qLower.includes("case-sum") || qLower.includes("0042")) {
        ansClass = "Synthesis";
        showActions = true;
        replyText = isArabicRequest
          ? "تحليل القضايا الموثقة... [ev:ev-01 • ص.1]"
          : "Case `CASE-2026-0042` Synthesis Report:\n\n* **Species:** Civil Litigation\n* **Damages:** AED 3,600,000.\n* **Evidentiary Posture:** 2 validated items [ev:ev-01 • p.1], 1 breach detected [ev:ev-03].\n* **SOL Clock:** 45 days remaining.";
      } else if (qLower.includes("obligations") || qLower.includes("ex-ob")) {
        ansClass = "Suggested Draft";
        showActions = true;
        replyText = "Draft Obligations Register:\n1. AED 120,000 Retainer payment. Due 12 June 2026 [clause:pay_sched • p.2].\n2. 30-Day Written Expiry notice.";
      } else {
        ansClass = "General Guidance";
        conf = "Medium";
        replyText = isArabicRequest
          ? `لقد سجلت استفسارك: "${query}". تم الربط مع [kb:uae_civil_code_246].`
          : `Understood. Indexed query regarding: *"${query}"*. Grounded against client files and statutory databases [kb:uae_civil_code_246].`;
      }

      setMessages(prev => [...prev, {
        id: "ai-" + Date.now().toString(),
        sender: "ai",
        text: replyText,
        answerClass: ansClass,
        confidence: conf,
        citationsCount: citCount,
        sourcesCount: 1,
        showActions: showActions
      }]);

      if (USE_MOCK_DATA) writeLogs("AI_COUNSEL", "QUERY_RUN", `Simulated automated QA response.`);
    }, 1200);
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputText.trim() && !aiReadOnly) {
        simulateAiResponse(inputText);
        setInputText("");
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleCreateTaskAction = () => {
    writeLogs(`${role} User`, "TASK_CREATE_FROM_AI", `Created new task from AI.`);
    setActionToast(t("ai.taskCreated"));
  };

  const handlePinFactAction = () => {
    writeLogs(`${role} User`, "FACT_PIN_FROM_AI", "Pinned insight.");
    setActionToast(t("ai.sentToDrafting"));
  };

  const renderFormattedText = (text: string) => {
    const regex = /\[(ev|clause|kb|rl):([^\]\s]+)(?: • ([^\]]+))?\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      const type = match[1];
      const id = match[2];
      const info = match[3] || "";

      parts.push(
        <span 
          key={match.index}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-orange-500/10 text-orange-600 hover:bg-orange-500 hover:text-white cursor-pointer transition-all mx-0.5 border border-orange-500/20"
        >
          <Zap size={8} />
          <span>{type === "ev" ? "EXHIBIT" : type === "clause" ? "PLAYBOOK" : "LAW"}</span>
          <span className="opacity-90">{id}</span>
        </span>
      );

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.map((part, index) => {
      if (typeof part === "string") {
        return part.split("\n").map((line, lIdx) => (
          <p key={`${index}-${lIdx}`} className="mb-2 last:mb-0 leading-relaxed text-[13px]">
            {line}
          </p>
        ));
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const menuThreads: Thread[] = USE_MOCK_DATA ? [
    { id: "t1", title: "Reviewing Smith v Gulf Liabilities", date: "Today", shared: true },
    { id: "t2", title: "Acme MSA Breach Context", date: "Yesterday" },
    { id: "t3", title: "Onboarding templates Q3", date: "May 20" }
  ] : [];

  if (isMobileViewport && !aiCounselOpen) {
    return null;
  }

  return (
    <motion.div 
      initial={false}
      animate={{ 
        width: aiCounselOpen ? (isMobileViewport ? "100vw" : `${aiCounselWidth}%`) : "0px",
        minWidth: aiCounselOpen ? (isMobileViewport ? "100vw" : "360px") : "0px",
        opacity: aiCounselOpen ? 1 : 0,
        pointerEvents: aiCounselOpen ? "auto" : "none"
      }}
      className={`flex flex-col h-screen select-none overflow-hidden ${isMobileViewport ? "fixed inset-0 z-50" : "relative z-30 shrink-0"} ${aiCounselOpen ? "" : "pointer-events-none"}`}
      aria-hidden={!aiCounselOpen}
    >
      <div data-testid="ai-counsel-panel" className={`${isMobileViewport ? "mx-0 my-0 rounded-none border-0 h-full" : "mx-4 my-6 rounded-3xl border border-slate-200/80"} flex-1 bg-white shadow-[0_20px_50px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden relative`}>
        
        {actionToast && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 animate-fade-in">
            <div className="flex items-center gap-2 rounded-xl bg-slate-900 text-white px-3 py-2 text-[11px] font-bold shadow-lg">
              <span>{actionToast}</span>
              <button type="button" onClick={() => setActionToast(null)} className="text-white/70 hover:text-white" aria-label="Dismiss">
                <X size={12} />
              </button>
            </div>
          </div>
        )}

        {/* A. COMPACT INSTRUMENT HEADER */}
        <div className="px-4 py-3 bg-white flex flex-col gap-2.5 border-b border-slate-100 shrink-0 shadow-sm relative z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/15 ring-1 ring-white/10">
                <Cpu size={14} />
              </div>
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 text-[10px] font-sans font-black uppercase tracking-[0.22em] text-slate-400">
                  <span>Vault-Co-Pilot</span>
                  <span className="h-1 w-1 rounded-full bg-slate-300" />
                  <span className="text-orange-600">{aiModeText}</span>
                </div>
                <div className="truncate text-[13px] font-sans font-black tracking-tight text-slate-900">{scopeText}</div>
                <div className="flex flex-wrap items-center gap-2.5 text-[8px] font-mono font-bold uppercase tracking-[0.16em] text-slate-400">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100/60 bg-emerald-50 px-2 py-1 text-emerald-600">
                    <Activity size={8} className="animate-pulse" />
                    {aiPolicySourceState}
                  </span>
                  <span>{aiPolicy.mode} / {aiPolicy.aiDepth}</span>
                  <span>Refreshed {aiPolicyLastRefreshAt?.toLocaleTimeString() ?? "pending"}</span>
                  {activeAlertsCount > 0 && <span className="text-orange-500">{activeAlertsCount} alerts</span>}
                </div>
              </div>
            </div>
            {isMobileViewport && (
              <button type="button" onClick={() => setAiCounselOpen(false)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors" aria-label="Close AI counsel">
                <X size={14} />
              </button>
            )}
          </div>
          {aiPolicyError && (
            <div className="truncate pl-12 text-[8px] font-mono text-orange-500/90">
              Operational control needs attention: {aiPolicyError}
            </div>
          )}
        </div>

        {/* B. CONTEXT ACTIONS RAIL */}
        <div className="px-4 py-1.5 border-b border-slate-100 bg-slate-50/20 flex items-center justify-between z-20 shrink-0">
          <button 
            onClick={() => {setShowPromptLibrary(!showPromptLibrary); setShowComposerMenu(false);}}
            className="text-[9px] font-sans font-bold flex items-center gap-1.5 text-slate-500 hover:text-slate-900 transition-all bg-white border border-slate-200/60 px-2.5 py-1 rounded-lg shadow-sm active:scale-95 uppercase tracking-tight"
          >
            <Lightbulb size={11} className={showPromptLibrary ? "text-orange-500" : ""} /> 
            Library <ChevronDown size={10} className={`transition-transform duration-200 ${showPromptLibrary ? "rotate-180" : ""}`} />
          </button>
          
          <div className="flex items-center gap-0.5">
            <button 
              onClick={() => {setShowThreadHistory(!showThreadHistory); setShowComposerMenu(false);}}
              className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-white rounded-lg transition-all"
              title="History"
            >
              <History size={13} />
            </button>
            <button 
              onClick={() => {setMessages([]); writeLogs(role, "CONVERSATION_RESET", "User cleared chat.");}}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
              title="Clear Thread"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>

        {/* PROMPT LIBRARY OVERLAY */}
        <AnimatePresence>
          {showPromptLibrary && (
            <motion.div 
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="absolute top-[88px] left-0 right-0 max-h-[60%] overflow-y-auto bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-2xl z-20 p-5 pt-3 no-scrollbar"
            >
              <div className="space-y-5">
                {[
                  { label: "MATTER_INTEL", prompts: ["Summarise case files", "Next legal steps", "Identify risk vectors"] },
                  { label: "CLIENT MODE", prompts: ["Financial WIP audit", "KYC status review", "Pending billables"] },
                  { label: "OPERATIONS", prompts: ["Operational control brief", "Conversion readiness review", "Force sync metadata"] }
                ].map(group => (
                  <div key={group.label}>
                    <h4 className="text-[9px] font-sans font-black uppercase text-slate-400 mb-2.5 tracking-widest">{group.label}</h4>
                    <div className="flex flex-wrap gap-2">
                      {group.prompts.map(p => (
                        <button 
                          key={p} 
                          onClick={() => handleChipClick(p)} 
                          className="px-3.5 py-2 text-[11px] font-bold text-left bg-slate-50 hover:bg-orange-50 border border-slate-200 hover:border-orange-200 text-slate-600 hover:text-orange-700 rounded-xl transition-all"
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button 
                onClick={() => setShowPromptLibrary(false)} 
                className="mt-6 w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-[11px] font-black text-white rounded-xl transition-colors uppercase tracking-widest"
              >
                Close Library
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* THREAD HISTORY OVERLAY */}
        <AnimatePresence>
          {showThreadHistory && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              className="absolute inset-4 top-[112px] bg-white border border-slate-200 rounded-3xl shadow-[0_32px_80px_rgba(0,0,0,0.15)] z-40 flex flex-col overflow-hidden"
            >
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-[11px] font-sans font-black text-slate-800 flex items-center gap-2 uppercase tracking-tight"><History size={16} className="text-orange-500"/> History</h3>
                <button onClick={() => setShowThreadHistory(false)} className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-slate-900 transition-colors"><X size={16}/></button>
              </div>
              <div className="p-4 border-b border-slate-100">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-3 text-slate-400" />
                  <input type="text" placeholder="Search interactions..." className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-[11px] font-sans font-bold text-slate-800 outline-none focus:border-orange-500/50 shadow-inner" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 no-scrollbar">
                {menuThreads.map(th => (
                  <div key={th.id} className={`p-4 rounded-2xl border transition-all cursor-pointer group ${th.id === 't1' ? 'border-orange-200 bg-orange-50/30' : 'border-transparent hover:bg-slate-50 hover:border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-tight">{th.date}</span>
                      {th.shared && <span className="text-[8px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-full border border-emerald-100 font-black uppercase">Shared</span>}
                    </div>
                    <p className={`text-[12px] font-black truncate pr-4 ${th.id === 't1' ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-950'}`}>{th.title}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* E. MESSAGE STREAM */}
        <div data-testid="ai-counsel-messages" className="flex-1 overflow-y-auto p-4 space-y-6 pt-6 no-scrollbar z-10 bg-slate-50/20" onClick={() => {setShowPromptLibrary(false); setShowComposerMenu(false); setShowThreadHistory(false);}}>
          <AnimatePresence initial={false}>
            {messages.map((msg, idx) => (
              <motion.div 
                key={msg.id} 
                initial={{ opacity: 0, y: 15, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: "easeOut", delay: idx * 0.05 }}
                className={`flex flex-col ${msg.sender === "lawyer" ? "items-end" : "items-start"}`}
              >
                {msg.sender === "ai" && msg.id !== "greet" && (
                  <div className="flex items-center gap-2 mb-2 ml-1 px-1 text-[9px] font-sans font-black">
                    <span className={`px-2 py-0.5 rounded-md text-white uppercase tracking-tighter ${
                      msg.answerClass === "Grounded" ? "bg-emerald-600" : msg.answerClass === "Synthesis" ? "bg-blue-600" : "bg-orange-600"
                    }`}>
                      {msg.answerClass}
                    </span>
                    <span className="text-slate-300">/</span>
                    <span className={`uppercase tracking-widest ${msg.confidence === "High" ? "text-emerald-600" : "text-amber-600"}`}>
                      C-{msg.confidence}
                    </span>
                  </div>
                )}

                <div 
                  className={`max-w-[94%] rounded-2xl p-5 text-[13px] shadow-sm select-text leading-relaxed font-medium transition-all ${
                    msg.sender === "lawyer" 
                      ? "bg-slate-900 text-white rounded-tr-sm shadow-xl shadow-slate-900/10" 
                      : "bg-white text-slate-700 border border-slate-200/60 rounded-tl-sm shadow-sm"
                  }`}
                >
                  {renderFormattedText(msg.text)}
                </div>

                {msg.sender === "ai" && msg.showActions && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-4 ml-2 flex flex-wrap gap-2"
                  >
                    {[
                      { icon: Layers, label: "Open in Drafting Studio", action: handlePinFactAction, color: "text-orange-600" },
                      { icon: CheckSquare, label: "Create Action Task", action: handleCreateTaskAction, color: "text-slate-700" }
                    ].map(btn => (
                      <button 
                        key={btn.label}
                        onClick={btn.action}
                        className="flex items-center gap-2.5 px-4 py-2 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 border border-slate-200/80 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all shadow-sm active:scale-95"
                      >
                        <btn.icon size={13} className={btn.color} /> {btn.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {isTyping && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-3 p-4 bg-white text-orange-600 rounded-2xl border border-slate-200/60 max-w-[150px] shadow-lg ml-1"
            >
              <div className="flex gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce [animation-duration:0.6s]"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce [animation-duration:0.6s] [animation-delay:0.2s]"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-bounce [animation-duration:0.6s] [animation-delay:0.4s]"></span>
              </div>
              <span className="text-[10px] font-sans font-black uppercase tracking-widest ml-1">{t("ai.thinking")}</span>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* C. INPUT COMPOSER (FLOATING PLATFORM) */}
        <div className="relative border-t border-slate-100 bg-white px-4 py-4 pb-5 z-30 shrink-0">
          
          {/* COMPOSER MENU OVERLAY */}
          <AnimatePresence>
            {showComposerMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                className="absolute bottom-[92%] left-4 w-64 bg-white border border-slate-200 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] p-2 mb-3 z-50 overflow-hidden"
              >
                <div className="space-y-0.5">
                  <button onClick={() => {setMessages([]); setShowComposerMenu(false);}} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-950 rounded-xl transition-colors text-left group">
                    <MessageSquare size={16} className="text-orange-500" />
                    <div className="flex-1">
                      <p className="text-[11px] font-black uppercase tracking-tight">New Conversation</p>
                      <p className="text-[8px] font-medium text-slate-400">Initialize fresh context thread</p>
                    </div>
                  </button>
                  <button onClick={() => {setShowThreadHistory(true); setShowComposerMenu(false);}} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-950 rounded-xl transition-colors text-left">
                    <History size={16} className="text-slate-400" />
                    <div className="flex-1">
                      <p className="text-[11px] font-black uppercase tracking-tight">History</p>
                      <p className="text-[8px] font-medium text-slate-400">Search previous consults</p>
                    </div>
                  </button>
                  <button onClick={() => {writeLogs(role, "THREAD_EXPORT", "User exported talk."); setShowComposerMenu(false);}} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-950 rounded-xl transition-colors text-left">
                    <Download size={16} className="text-slate-400" />
                    <div className="flex-1">
                      <p className="text-[11px] font-black uppercase tracking-tight">Export Thread</p>
                      <p className="text-[8px] font-medium text-slate-400">Download context as PDF/Doc</p>
                    </div>
                  </button>
                  <div className="h-px bg-slate-100 my-1 mx-2" />
                  <button onClick={() => {writeLogs(role, "LEAD_CREATE", "Created CRM lead."); setShowComposerMenu(false);}} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 rounded-xl transition-colors text-left">
                    <TrendingUp size={16} className="text-emerald-500" />
                    <div className="flex-1">
                      <p className="text-[11px] font-black uppercase tracking-tight">Create CRM Record</p>
                      <p className="text-[8px] font-medium text-slate-400">Convert thread into a CRM record</p>
                    </div>
                  </button>
                  <button onClick={() => {setMessages([]); setShowComposerMenu(false);}} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 text-slate-600 hover:text-red-600 rounded-xl transition-colors text-left">
                    <Trash2 size={16} className="text-red-400" />
                    <div className="flex-1">
                      <p className="text-[11px] font-black uppercase tracking-tight">Clear History</p>
                      <p className="text-[8px] font-medium text-slate-400">Permanently wipe all interactions</p>
                    </div>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="rounded-[24px] border border-slate-200/80 bg-slate-50/80 px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] transition-all focus-within:border-orange-500/40 focus-within:bg-white focus-within:ring-4 focus-within:ring-orange-500/5">
            {aiReadOnly && (
              <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-bold text-red-700">
                {aiReadOnlyReason ?? "AI credits depleted. Purchase credits to continue."}
              </div>
            )}
            <div className="flex items-end gap-2.5">
            <button 
              type="button"
              onClick={() => {setShowComposerMenu(!showComposerMenu); setShowPromptLibrary(false);}}
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-all ${showComposerMenu ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' : 'text-slate-400 hover:text-slate-900 hover:bg-white/90 border border-transparent hover:border-slate-200/70'}`}
            >
              <Plus size={18} className={`transition-transform duration-300 ${showComposerMenu ? 'rotate-45' : ''}`} />
            </button>
            <div className="min-w-0 flex-1">
              <textarea 
                data-testid="ai-counsel-input"
                ref={textareaRef}
                value={inputText}
                onChange={handleTextareaInput}
                onKeyDown={handleKeyDown}
                placeholder={aiReadOnly ? "AI read-only until credits are replenished" : "Ask VAULT-CO-PILOT anything..."}
                disabled={aiReadOnly}
                className="min-h-[44px] max-h-[220px] w-full resize-none overflow-hidden bg-transparent px-1 py-2 text-[14px] font-sans font-semibold leading-relaxed text-slate-800 outline-none placeholder:text-slate-400"
                rows={1}
              />
            </div>
            <div className="flex shrink-0 items-center gap-2 pb-0.5">
              <button 
                type="button"
                onClick={() => setIsVoiceActive(!isVoiceActive)}
                className={`flex h-10 w-10 items-center justify-center rounded-2xl transition-all ${isVoiceActive ? 'bg-orange-600 text-white animate-pulse shadow-lg shadow-orange-600/20' : 'text-slate-400 hover:text-slate-900 hover:bg-white/90 border border-transparent hover:border-slate-200/70'}`}
                title="Dictation Support"
              >
                <Mic size={18} />
              </button>
              <button 
                data-testid="ai-counsel-send"
                type="button"
                onClick={() => {
                  if(inputText.trim()){
                    if (aiReadOnly) {
                      setActionToast(aiReadOnlyReason ?? "AI credits depleted. Purchase credits to continue.");
                      return;
                    }
                    simulateAiResponse(inputText);
                    setInputText("");
                    if(textareaRef.current) textareaRef.current.style.height = 'auto';
                  }
                }}
                disabled={!inputText.trim() || aiReadOnly}
                className={`flex h-11 w-11 items-center justify-center rounded-2xl transition-all shadow-lg active:scale-95 ${
                  inputText.trim() && !aiReadOnly
                    ? 'bg-slate-900 text-white hover:bg-slate-800' 
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                }`}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
            <div className="mt-2.5 flex items-center justify-between gap-3 px-1">
              <div className="flex items-center gap-3 min-w-0">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className={`flex h-4 w-4 items-center justify-center rounded border transition-all ${isGlobalFirmMode ? 'bg-orange-600 border-orange-600' : 'bg-white border-slate-300 group-hover:border-slate-400'}`}>
                    {isGlobalFirmMode && <FileCheck size={9} className="text-white" />}
                  </div>
                  <input type="checkbox" checked={isGlobalFirmMode} onChange={(e) => setIsGlobalFirmMode(e.target.checked)} className="hidden" />
                  <span className="text-[8px] font-sans font-black uppercase tracking-[0.16em] text-slate-400 transition-colors group-hover:text-slate-600">Firm Portfolio Scan</span>
                </label>
                <span className="truncate text-[8px] font-mono font-bold uppercase tracking-[0.14em] text-slate-300">Grounded to approved firm records</span>
              </div>
              {activeMatterId && (
                <span className="shrink-0 rounded-full border border-orange-500/20 bg-orange-500/10 px-2.5 py-1 text-[8px] font-sans font-black uppercase tracking-[0.16em] text-orange-600">
                  Grounded
                </span>
              )}
            </div>
          </div>
        </div>

      </div>
    </motion.div>
  );
};
